import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheck, CircleX, ExternalLink } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MeterBar } from '../../../components/design-system/analytics';
import { SectionCard, StatusBadge, formatNumber } from '../../../components/design-system/admin';
import { MarketBusinessLocation, MarketData } from '../../../common/hooks/use-market-api.hook';
import { LISTING_FIELDS, ListingField, listingCompleteness, ratingRows } from '../../../modules/vendor/market/market-analysis';
import { profileInsights } from '../../../modules/vendor/market/market-insights';
import { Stars } from './overview-view';
import { Nothing, OutLink, Reading, SortHead, TableFrame, sortRows, useSort } from './market-shared';

const GOOGLE_BUSINESS = 'https://business.google.com/';

/**
 * The Google Business Profile.
 *
 * Surfaces and furnishings are chosen in a showroom, so this is where a
 * customer forms their first opinion — and none of it is visible from the
 * website the rest of these pages read. The reading leads with whether it is
 * claimed, then the rating and how the stars split, then how complete the
 * listing is, then every branch with the worst-rated first.
 */
export const ProfileView: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const b = data.business;
    const specs = useMemo(() => profileInsights(b), [b]);

    if (!b) return <Nothing title={t('MARKET.PROFILE.NONE')} />;

    /*
     * Searched and found nothing. Its own state, not the empty one: a showroom
     * with no Google listing is invisible in local search, which is a thing
     * they can fix this afternoon.
     */
    if (b.notFound) {
        return (
            <div className="flex flex-col items-start gap-4 rounded-xl border bg-card p-8" data-testid="business-profile-missing">
                <StatusBadge tone="danger">{t('MARKET.PROFILE.MISSING')}</StatusBadge>
                <p className="max-w-[560px] text-sm leading-relaxed text-muted-foreground">{t('MARKET.PROFILE.MISSING_BODY', { name: b.searchedFor || '—' })}</p>
                <Button variant="outline" asChild>
                    <a href={GOOGLE_BUSINESS} target="_blank" rel="noopener noreferrer">
                        {t('MARKET.PROFILE.MISSING_ACTION')}
                        <ExternalLink data-icon="inline-end" />
                    </a>
                </Button>
            </div>
        );
    }

    const breakdown = ratingRows(b.ratingBreakdown);
    const totalStars = breakdown?.reduce((s, r) => s + r.count, 0) ?? 0;
    const complete = listingCompleteness(b);
    const locations = b.locations ?? [];

    const fieldValue: Record<ListingField, React.ReactNode> = {
        category: b.category,
        address: b.address,
        phone: b.phone,
        website: b.website ? <OutLink href={b.website}>{b.website.replace(/^https?:\/\//, '')}</OutLink> : null,
        hours: b.workTime ? t('BUSINESS.PROFILE.HOURS_SET') : null,
        photos: b.photos ? formatNumber(b.photos) : null,
    };

    return (
        <div className="flex flex-col gap-6">
            <Reading specs={specs} />

            <div className="grid gap-4 lg:grid-cols-3">
                <SectionCard title={t('MARKET.PROFILE.RATING')} description={t('MARKET.PROFILE.RATING_HINT')} testId="business-rating">
                    <div className="flex items-baseline gap-2">
                        <span className="font-heading text-4xl leading-none font-medium tabular-nums">{b.rating ? formatNumber(b.rating, 1) : '—'}</span>
                        <Stars value={b.rating} />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                        {locations.length > 1
                            ? t('MARKET.PROFILE.REVIEWS_ALL', { total: formatNumber(b.totalReviews ?? 0), branches: locations.length })
                            : t('BUSINESS.PROFILE.REVIEWS_COUNT', { count: b.reviews ?? 0, n: formatNumber(b.reviews ?? 0) })}
                    </p>
                    {breakdown && (
                        <ul className="mt-4 flex flex-col gap-1.5 border-t pt-4" aria-label={t('BUSINESS.PROFILE.BREAKDOWN')}>
                            {breakdown.map((r) => (
                                <li key={r.star} className="flex items-center gap-2 text-xs">
                                    <span className="w-3 tabular-nums text-muted-foreground">{r.star}</span>
                                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                                        <div
                                            className={cn('h-full rounded-full', r.star <= 2 && r.count > 0 ? 'bg-destructive' : 'bg-foreground/70')}
                                            style={{ width: `${totalStars ? (r.count / totalStars) * 100 : 0}%` }}
                                        />
                                    </div>
                                    <span className="w-8 text-end tabular-nums text-muted-foreground">{formatNumber(r.count)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </SectionCard>

                <SectionCard
                    title={
                        b.mapsUrl ? (
                            <OutLink href={b.mapsUrl}>{b.title || t('MARKET.PROFILE.LISTING')}</OutLink>
                        ) : (
                            b.title || t('MARKET.PROFILE.LISTING')
                        )
                    }
                    description={t('BUSINESS.PROFILE.COMPLETE_HINT')}
                    className="lg:col-span-2"
                    testId="business-listing"
                >
                    {complete && (
                        <div className="mb-4 flex items-center justify-between gap-3">
                            <span className="text-sm">
                                {t('BUSINESS.PROFILE.COMPLETE', { filled: complete.filled.length, total: LISTING_FIELDS.length })}
                            </span>
                            <MeterBar value={(complete.filled.length / LISTING_FIELDS.length) * 100} label={`${complete.filled.length}/${LISTING_FIELDS.length}`} width="w-24" />
                        </div>
                    )}
                    <dl className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                        {LISTING_FIELDS.map((f) => {
                            const ok = complete?.filled.includes(f);
                            return (
                                <div key={f} className="flex min-w-0 items-start gap-2 text-sm">
                                    {ok ? <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" /> : <CircleX className="mt-0.5 size-4 shrink-0 text-destructive" />}
                                    <div className="min-w-0">
                                        <dt className="text-xs text-muted-foreground">{t(`BUSINESS.PROFILE.FIELD.${f.toUpperCase()}`)}</dt>
                                        <dd className="min-w-0 break-words">{ok ? fieldValue[f] : <span className="text-muted-foreground">{t('BUSINESS.PROFILE.MISSING_VALUE')}</span>}</dd>
                                    </div>
                                </div>
                            );
                        })}
                    </dl>
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                        <div className="min-w-0">
                            <StatusBadge tone={b.isClaimed ? 'success' : 'danger'}>{t(b.isClaimed ? 'MARKET.PROFILE.CLAIMED_YES' : 'MARKET.PROFILE.CLAIMED_NO')}</StatusBadge>
                            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                {b.unclaimed
                                    ? t('MARKET.PROFILE.UNCLAIMED_COUNT', { unclaimed: b.unclaimed })
                                    : t(b.isClaimed ? 'MARKET.PROFILE.CLAIMED_HINT' : 'MARKET.PROFILE.UNCLAIMED_HINT')}
                            </p>
                        </div>
                        {(b.isClaimed === false || (b.unclaimed ?? 0) > 0) && (
                            <Button variant="outline" size="sm" asChild>
                                <a href={GOOGLE_BUSINESS} target="_blank" rel="noopener noreferrer">
                                    {t('BUSINESS.PROFILE.CLAIM')}
                                    <ExternalLink data-icon="inline-end" />
                                </a>
                            </Button>
                        )}
                    </div>
                </SectionCard>
            </div>

            {locations.length > 1 && <Branches locations={locations} />}
        </div>
    );
};

type BranchSort = 'city' | 'rating' | 'reviews' | 'photos';

/**
 * Every branch Google knows, each with its own stars — worst first. A
 * chain's worst-rated showroom is invisible in an average and is usually the
 * one costing them the enquiry.
 */
const Branches: React.FC<{ locations: MarketBusinessLocation[] }> = ({ locations }) => {
    const { t } = useTranslation();
    const { sort, toggle } = useSort<BranchSort>('rating', 'asc');
    const rows = useMemo(() => {
        const get: Record<BranchSort, (l: MarketBusinessLocation) => number | string | null | undefined> = {
            city: (l) => l.city || l.title,
            rating: (l) => l.rating,
            reviews: (l) => l.reviews,
            photos: (l) => l.photos,
        };
        return sortRows(locations, get[sort.key], sort.dir);
    }, [locations, sort]);

    return (
        <SectionCard flush title={t('MARKET.PROFILE.BRANCHES')} description={t('MARKET.PROFILE.BRANCHES_HINT')} testId="business-branches">
            <TableFrame className="border-t">
                <thead className="border-b text-xs text-muted-foreground">
                    <tr>
                        <SortHead align="start" label={t('BUSINESS.PROFILE.BRANCH')} active={sort.key === 'city'} dir={sort.dir} onClick={() => toggle('city', 'asc')} />
                        <SortHead label={t('MARKET.PROFILE.RATING')} active={sort.key === 'rating'} dir={sort.dir} onClick={() => toggle('rating', 'asc')} />
                        <SortHead label={t('MARKET.PROFILE.REVIEWS')} active={sort.key === 'reviews'} dir={sort.dir} onClick={() => toggle('reviews')} />
                        <SortHead label={t('MARKET.PROFILE.PHOTOS')} active={sort.key === 'photos'} dir={sort.dir} onClick={() => toggle('photos')} />
                        <th className="p-3 text-end font-medium">{t('MARKET.PROFILE.CLAIMED')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((l, i) => (
                        <tr key={l.mapsUrl || `${l.title}-${i}`} className="border-b last:border-b-0 hover:bg-muted/30">
                            <td className="max-w-[320px] p-3 align-top">
                                {l.mapsUrl ? <OutLink href={l.mapsUrl} className="font-medium">{l.city || l.title || '—'}</OutLink> : <span className="font-medium">{l.city || l.title || '—'}</span>}
                                {l.address && <p className="mt-0.5 truncate text-xs text-muted-foreground">{l.address}</p>}
                            </td>
                            <td className="p-3 text-end align-top whitespace-nowrap">
                                <span className={cn('tabular-nums', l.rating != null && l.rating < 4 && 'text-destructive')}>{l.rating ? formatNumber(l.rating, 1) : '—'}</span>
                            </td>
                            <td className="p-3 text-end align-top tabular-nums">{formatNumber(l.reviews ?? 0)}</td>
                            <td className="p-3 text-end align-top tabular-nums">{formatNumber(l.photos ?? 0)}</td>
                            <td className="p-3 text-end align-top">
                                {l.isClaimed == null ? (
                                    <span className="text-muted-foreground">—</span>
                                ) : (
                                    <StatusBadge tone={l.isClaimed ? 'success' : 'danger'}>{t(l.isClaimed ? 'MARKET.PROFILE.CLAIMED_YES' : 'MARKET.PROFILE.CLAIMED_NO')}</StatusBadge>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </TableFrame>
        </SectionCard>
    );
};

/** What a site runs. Shown inside the competitors view. */
export const Technologies: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const tech = data.technologies?.technologies ?? [];
    const domain = data.technologies?.domain;

    if (!tech.length) return null;

    return (
        <SectionCard
            // The stack is read for one domain at a time, and it is usually a
            // competitor's rather than the vendor's own — so name it, or the
            // list reads as a claim about their own site.
            title={
                <span className="flex flex-wrap items-baseline gap-2">
                    {t('MARKET.TECH.TITLE')}
                    {domain && <span className="text-xs font-normal text-muted-foreground">{domain}</span>}
                </span>
            }
            description={t('MARKET.TECH.SUBTITLE')}
        >
            <div className="flex flex-wrap gap-1.5">
                {tech.map((name) => (
                    <Badge key={name} variant="outline">
                        {name}
                    </Badge>
                ))}
            </div>
        </SectionCard>
    );
};
